import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

// Assuming these components are correctly imported from their respective files
import RoadmapDetails from "./RoadmapDetails";
import { create } from "zustand";
import useStartupStore from "../stores/startupStore";
import OpeningRoles from "../components/Startups/OpeningRoles";
import fetchApi from "../components/Lib/api";
import TableOfContentsFloating from "./TOC";
import GuideEditor from "./GuideEditor";
import DocumentUpload from "./DocumentUpload";
import GoogleDrivePickerAndUploader from "../components/Lib/FilesPickerAndUploader";
import TaskBoardsManager from "./TaskBoardsManager";
import SubcategoryFeedback from "./SubcategoryFeedback";
import ContainerButton from "./ContainerButton";
import Mixpanel from "../components/Lib/mixpanel";
import { t } from "i18next";
import ChatDocumentUpload from "./ChatDocumentUpload";

// Zustand store (ensure this is correctly defined and imported)
export const useRoadmapStore = create((set, get) => ({
  date: null,
  setDate: (newDate) => set({ date: newDate }),
  endDate: null,
  setEndDate: (newEndDate) => set({ endDate: newEndDate }),
  duration: "0",
  setDuration: (newDuration) => set({ duration: newDuration }),
  opened: false,
  setOpened: (isOpened) => set({ opened: isOpened }),
  loading: false,
  setLoading: (isLoading) => set({ loading: isLoading }),

  handleUpdate: async (slug, setProgram) => {
    const { date, duration, setOpened, setLoading, endDate } = get();
    setOpened(true);
    setLoading(true);
    console.log("Updating roadmap with:", { slug, duration, date, endDate }); // Log data being sent
    try {
      const response = await fetchApi(
        `/startup_entities/${slug}/update_roadmap_duration`,
        "PATCH",
        { roadmap_duration: duration, start_date: date, end_date: endDate }
      );

      if (response.ok) {
        const res = await response.json();
        console.log("Roadmap update response:", res);
        // Ensure the response structure matches what setProgram expects
        if (res.program) {
          setProgram({ ...res.program });
        } else {
          console.warn("API response did not contain 'program' data.");
          // Potentially update program state based on available data or refetch
        }
      } else {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Failed to parse error JSON" })); // Catch JSON parsing errors
        console.error(
          "Roadmap update failed:",
          response.status,
          errorData.error
        );
      }
    } catch (error) {
      console.error("Error during roadmap update API call:", error);
    } finally {
      setOpened(false);
      setLoading(false);
    }
  },
}));

// --- Styled Components ---

const StartupBodyContainer = styled.div`
  display: grid;
  /* Define 3 columns: TOC (auto width), Main Content (flexible), Right Col (fixed span) */
  /* Adjust based on whether RightColumn (showTask=false) is shown */
  /* --- MODIFIED: Reverted fractions to 8fr (main) / 3fr (right), reduced gap --- */
  grid-template-columns: ${({ showTask }) =>
    showTask
      ? "auto 1fr" /* When task view: TOC and Task Board */
      : "auto minmax(0, 8fr) minmax(0, 3fr)"}; /* Default: TOC | Main | Right */

  gap: 10px; /* --- MODIFIED: Reduced gap --- */
  min-height: 150vh;
  height: 100%;
  align-items: start; /* Align items to the top */
  padding: 0 10px; /* Add some horizontal padding if needed */

  @media (max-width: 1024px) {
    /* Stack columns on smaller screens */
    grid-template-columns: 1fr; /* Single column */
    gap: 30px; /* Adjust vertical gap */
  }
`;

// --- Wrapper for the Table of Contents ---
// Now a direct child of StartupBodyContainer
const StickyTOCWrapper = styled.div`
  grid-column: 1 / 2; /* Takes the first column */
  position: sticky;
  top: 80px; /* Adjust based on your header height */
  align-self: start; /* Stick to the top of its grid area */
  z-index: 10;
  max-height: calc(100vh - 100px); /* Prevent exceeding viewport height */
  overflow-y: auto; /* Allow scrolling within TOC if needed */

  @media (max-width: 1024px) {
    /* Reset sticky behavior and layout for smaller screens */
    grid-column: 1 / -1; /* Take full width */
    position: static;
    max-height: none;
    overflow-y: visible;
    order: -1; /* Move TOC above content on mobile */
  }
`;

// --- Container for the main content (RoadmapDetails or TaskBoardsManager) ---
// Now a direct child of StartupBodyContainer
const MainContentWrapper = styled.div`
  /* Takes the second column */
  grid-column: 2 / 3;
  min-width: 0; /* Important for grid/flex children */

  @media (max-width: 1024px) {
    grid-column: 1 / -1; /* Take full width */
    order: 0; /* Ensure it comes after TOC if TOC order is -1 */
  }
`;

// --- Right Column ---
// Now a direct child of StartupBodyContainer
const RightColumn = styled.div`
  border-radius: 8px;
  /* Takes the third column */
  grid-column: 3 / 4;
  display: flex;
  flex-direction: column;
  gap: 10px;

  /* --- Sticky Positioning --- */
  position: sticky;
  top: 80px; /* Adjust */
  align-self: start;
  /* --- End Sticky Positioning --- */

  @media (max-width: 1024px) {
    /* Stack below other content on smaller screens */
    grid-column: 1 / -1; /* Span full width */
    position: static; /* Disable sticky */
    max-height: none;
    overflow-y: visible;
    margin-top: 30px; /* Add space above when stacked */
    order: 1; /* Ensure it comes last */
  }
`;

// --- StartupBody Component ---

const StartupBody = () => {
  const guideEditorRef = useRef(null);
  const [showTask, setShowTask] = useState(false);
  const [rightTab, setRightTab] = useState("files");

  // Make sure useStartupStore provides all needed state and actions
  const { selectedProgram, program, isMember, isOwner, setProgram } =
    useStartupStore((state) => ({
      program: state.program,
      selectedProgram: state.selectedProgram,
      isMember: state.isMember,
      isOwner: state.isOwner,
      setProgram: state.setProgram,
    }));

  // Refined check for valid selection and data existence
  const isValidSelection = React.useMemo(() => {
    return Boolean(
      // Use Boolean for clearer true/false
      program &&
        program.categories &&
        Array.isArray(program.categories) && // Check if it's an array
        selectedProgram &&
        typeof selectedProgram.cid === "number" && // Check types
        typeof selectedProgram.sid === "number" &&
        selectedProgram.cid >= 0 &&
        selectedProgram.sid >= 0 && // Check indices validity
        program.categories.length > selectedProgram.cid &&
        program.categories[selectedProgram.cid] &&
        program.categories[selectedProgram.cid].sub_categories &&
        Array.isArray(program.categories[selectedProgram.cid].sub_categories) && // Check sub-array
        program.categories[selectedProgram.cid].sub_categories.length >
          selectedProgram.sid &&
        program.categories[selectedProgram.cid].sub_categories[
          selectedProgram.sid
        ]
    );
  }, [program, selectedProgram]); // Recalculate only when program or selection changes

  return (
    // Pass showTask to the container to adjust grid columns
    <StartupBodyContainer showTask={showTask}>
      <StickyTOCWrapper showTask={showTask}>
        <TableOfContentsFloating />
      </StickyTOCWrapper>

      {/* --- Main Content Area (Roadmap/Taskboard) --- */}
      <MainContentWrapper>
        {isValidSelection && showTask ? (
          <TaskBoardsManager
            // Ensure key is unique and stable
            key={`taskboard-${selectedProgram.cid}-${selectedProgram.sid}`}
            toggle={() => setShowTask(false)} // Action to switch back
            name={
              program.categories[selectedProgram.cid].sub_categories[
                selectedProgram.sid
              ].name
            }
            subCategoryId={
              program.categories[selectedProgram.cid].sub_categories[
                selectedProgram.sid
              ].id
            }
          />
        ) : (
          // Render RoadmapDetails if not showing task or if selection/data is invalid
          <RoadmapDetails setShowTask={setShowTask} />
        )}
      </MainContentWrapper>
      {/* --- Right Column (Conditionally Rendered) --- */}
      {!showTask && (
        <RightColumn>
          {isOwner || isMember ? (
            <>
              {/* AI Smart Assistant */}
              <ChatDocumentUpload isOpen={true} anchored="inline" />
              <GuideEditor
                editorRef={guideEditorRef}
                // Add a more robust key, maybe based on program ID if available
                key={`guide-editor-${program?.id}-${selectedProgram?.cid}-${selectedProgram?.sid}`}
              />
              {/* Commit/Complete controls outside tabs */}
              <DocumentUpload />
              <ContainerButton
                buttonText={t("taskboard_button.taskboard")}
                hidePlus={true}
                action={() => {
                  setShowTask(true);
                  Mixpanel.button(t("taskboard_button.taskboard"), {
                    location: "Project Page Right Column",
                  });
                }}
                // disabled={!isValidSelection}
              />
            </>
          ) : (
            <OpeningRoles />
          )}

          {/* Files/Comments Tabbed Card */}
          <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
            <div className="flex items-center justify-between px-3 py-3 border-b border-gray-100">
              <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                <button
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    rightTab === "files"
                      ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                  onClick={() => setRightTab("files")}
                  aria-pressed={rightTab === "files"}
                >
                  Files
                </button>
                <button
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    rightTab === "comments"
                      ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                  onClick={() => setRightTab("comments")}
                  aria-pressed={rightTab === "comments"}
                >
                  Comments
                </button>
              </div>
            </div>
            <div className="p-3">
              {rightTab === "files" && <GoogleDrivePickerAndUploader />}
              {rightTab === "comments" && isValidSelection && (
                <SubcategoryFeedback
                  projectOwner={isOwner}
                  key={`feedback-${selectedProgram.cid}-${selectedProgram.sid}`}
                  subCategoryName={
                    program.categories[selectedProgram.cid].sub_categories[
                      selectedProgram.sid
                    ].name
                  }
                  subCategoryId={
                    program.categories[selectedProgram.cid].sub_categories[
                      selectedProgram.sid
                    ].id
                  }
                />
              )}
            </div>
          </div>

          {/* Example: Include RoadmapCompletionDuration if it belongs here */}
          {/* <RoadmapCompletionDuration slug={program?.slug} /> */}
        </RightColumn>
      )}

      {/* <ProposalSubmission /> */}
    </StartupBodyContainer>
  );
};

export default StartupBody;
